import { Alert, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { KPI_COLORS } from "./fulfillment-kpi.styles";
import KPITile from "../analytics/kpi-tile";
import KpiBarChart from "./kpi-bar-chart";
import KpiListTable from "./kpi-list-table";
import KpiSection from "./kpi-section";
import {
  LOAD_STATE,
  formatHubspotDay,
  formatMonthLabel,
  formatPercent,
  lastCompleteRow,
  toBarSeries
} from "./fulfillment-kpi.helpers";

// KPI 1b: campaign start within 30 days of the contract, from HubSpot
// (Asana 1219612776200513). `data` is the /campaign-start response, or
// LOAD_STATE.LOADING / LOAD_STATE.FAILED (request failed, `onRetry`).
const CampaignStartSection = ({
  data,
  thresholds,
  language,
  runningLabel,
  onRetry
}) => {
  const { t } = useTranslation();
  const days = thresholds.kpi1Days;

  const title = t("fulfillmentKpis.campaign.title", { days });
  const help = t("fulfillmentKpis.campaign.help", { days });

  if (data === LOAD_STATE.LOADING || data === LOAD_STATE.FAILED) {
    return (
      <KpiSection
        title={title}
        help={help}
        notice={
          data === LOAD_STATE.FAILED ? (
            <Alert variant="danger" className="mb-0">
              {t("fulfillmentKpis.loadError")}
              <Button variant="link" size="sm" onClick={onRetry}>
                {t("fulfillmentKpis.retry")}
              </Button>
            </Alert>
          ) : (
            <Spinner animation="border" size="sm" />
          )
        }
      />
    );
  }

  if (!data?.available) {
    return (
      <KpiSection
        title={title}
        help={help}
        notice={
          <Alert variant="secondary" className="mb-0">
            {t(
              data?.reason === "no-access" || data?.reason === "no-token"
                ? "fulfillmentKpis.campaign.noAccess"
                : "fulfillmentKpis.campaign.unavailable"
            )}
          </Alert>
        }
      />
    );
  }

  const rows = Array.isArray(data.rows) ? data.rows : [];
  const withoutStart = Array.isArray(data.withoutStart)
    ? data.withoutStart
    : [];
  const last = lastCompleteRow(rows, "share");

  return (
    <KpiSection
      title={title}
      help={help}
      notice={
        data.truncated && (
          <Alert variant="warning" className="py-2 small">
            {t("fulfillmentKpis.campaign.truncated")}
          </Alert>
        )
      }
      tiles={
        <KPITile
          data={formatPercent(last?.share, language)}
          title={t("fulfillmentKpis.campaign.tile", {
            days,
            month: last ? formatMonthLabel(last.month, language) : "–",
            target: thresholds.kpi1Target,
            median: last?.medianDays ?? "–"
          })}
          bulletColor={KPI_COLORS.campaign}
        />
      }
      chart={
        <KpiBarChart
          colors={[KPI_COLORS.campaign]}
          language={language}
          runningLabel={runningLabel}
          target={thresholds.kpi1Target}
          targetLabel={t("fulfillmentKpis.target", {
            value: thresholds.kpi1Target
          })}
          series={[
            {
              name: t("fulfillmentKpis.campaign.series"),
              data: toBarSeries(
                rows,
                "share",
                KPI_COLORS.campaign,
                KPI_COLORS.campaignRunning
              )
            }
          ]}
          tooltipFor={(index) =>
            t("fulfillmentKpis.campaign.tooltip", rows[index] || {})
          }
        />
      }
    >
      <KpiListTable
        title={t("fulfillmentKpis.campaign.withoutTitle", {
          days: thresholds.listMinDays
        })}
        rows={withoutStart}
        rowKey={(row) => row.dealId}
        columns={[
          { key: "deal", label: t("fulfillmentKpis.campaign.deal") },
          {
            key: "contractDate",
            label: t("fulfillmentKpis.campaign.contractDate")
          },
          {
            key: "days",
            label: t("fulfillmentKpis.campaign.days"),
            center: true
          },
          { key: "stage", label: t("fulfillmentKpis.campaign.stage") }
        ]}
        cells={(row) => ({
          deal: (
            <a href={row.url} target="_blank" rel="noreferrer">
              {row.dealName || row.dealId}
            </a>
          ),
          contractDate: formatHubspotDay(row.contractDate),
          days: row.daysSinceContract,
          stage: row.stage
        })}
      />
    </KpiSection>
  );
};

export default CampaignStartSection;
